import React from 'react';
import { CopyRowPayload, InputTableColumn, RepeatRowsPayload } from '../types';
import { CellValueResolver } from './cell-value-resolver';
import { AritoIcon } from '@/components/custom/arito/icon';

export interface ContextMenuOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface ContextMenuBuilderOptions<T = any> {
  contextMenu: {
    rowData: T | null;
    columnField: string | null;
  };
  columns: InputTableColumn[];
  getRowId?: (row: T) => string | number;
  onCopyData?: (payload: CopyRowPayload<T>) => void;
  onRepeatForRows?: (payload: RepeatRowsPayload<T>) => void;
  rowsCount: number;
}

/**
 * Builder for creating context menu options
 */
export class ContextMenuBuilder {
  /**
   * Creates all available context menu options
   */
  static buildOptions<T = any>(options: ContextMenuBuilderOptions<T>): ContextMenuOption[] {
    const { contextMenu, columns, getRowId, onCopyData, onRepeatForRows, rowsCount } = options;

    const menuOptions: ContextMenuOption[] = [];

    // Copy data option
    if (onCopyData) {
      menuOptions.push(this.buildCopyDataOption(contextMenu, columns, getRowId, onCopyData));
    }

    // Repeat rows option (only if there are multiple rows)
    if (onRepeatForRows && rowsCount > 1) {
      menuOptions.push(this.buildRepeatRowsOption(contextMenu, columns, onRepeatForRows));
    }

    return menuOptions;
  }

  /**
   * Creates the copy data context menu option
   */
  private static buildCopyDataOption<T = any>(
    contextMenu: { rowData: T | null; columnField: string | null },
    columns: InputTableColumn[],
    getRowId: ((row: T) => string | number) | undefined,
    onCopyData: (payload: CopyRowPayload<T>) => void
  ): ContextMenuOption {
    return {
      id: 'copy-data',
      label: 'Sao chép dữ liệu',
      icon: <AritoIcon icon={314} />,
      onClick: () => {
        if (!contextMenu.rowData) return;

        const resolvedValue = CellValueResolver.resolveValue({
          rowData: contextMenu.rowData,
          columnField: contextMenu.columnField || '',
          columns,
          getRowId
        });

        onCopyData({
          row: contextMenu.rowData,
          columns,
          columnValues: resolvedValue.value as Record<string, unknown>,
          columnField: resolvedValue.columnField,
          columnValue: resolvedValue.columnValue
        });
      }
    };
  }

  /**
   * Creates the repeat rows context menu option
   */
  private static buildRepeatRowsOption<T = any>(
    contextMenu: { rowData: T | null; columnField: string | null },
    columns: InputTableColumn[],
    onRepeatForRows: (payload: RepeatRowsPayload<T>) => void
  ): ContextMenuOption {
    return {
      id: 'repeat-rows',
      label: 'Lặp lại cho các dòng',
      icon: <AritoIcon icon={40} />,
      onClick: () => {
        if (!contextMenu.rowData) return;

        onRepeatForRows({
          row: contextMenu.rowData,
          columnField: contextMenu.columnField || null,
          columns
        });
      }
    };
  }
}
